import { getErrorMessage } from '@/helpers/auth';
import { handleServiceError, createServiceResponse, extractMovementErrorCode } from '@/helpers/errorHandling';
import { t, te } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';
import { supabase } from '@/supabase';
import type { ServiceResponse } from '@/types/core';
import type { MovementGetAllParams, MovementHardwareSaveData, MovementListResponse, MovementResponse, MovementSaveData } from '@/types/movement';
import { formatISO } from 'date-fns';
import ServiceDomains from '@/constants/ServiceDomains';

/**
 * Creates a new movement with associated hardware items using a database transaction
 *
 * @param movement The movement data to create
 * @param hardwares The hardware items to associate with the movement
 * @returns ServiceResponse with the created movement data or error
 */
export async function createMovement(movement: MovementSaveData, hardwares: MovementHardwareSaveData[]): Promise<ServiceResponse<string>> {
  try {
    const { data, error } = await supabase.rpc('create_movement_with_hardware', {
      p_date: movement.date,
      p_employee_uid: movement.employee_uid,
      p_foreman_uid: movement.foreman_uid,
      p_hardware_items: hardwares,
      p_project_uid: movement.project_uid,
      p_type: movement.type,
    });

    if (error) {
      return createServiceResponse(null, error, ServiceDomains.Movement);
    }

    // Return the created movement UID
    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Updates an existing movement with new hardware items using a database transaction
 *
 * @param movementUid The UID of the movement to update
 * @param movement The updated movement data
 * @param hardwares The new hardware items to associate with the movement
 * @returns ServiceResponse with the updated movement data or error
 */
export async function updateMovement(movementUid: string, movement: MovementSaveData, hardwares: MovementHardwareSaveData[]): Promise<ServiceResponse<string>> {
  try {
    // Call the database function that handles the transaction
    const { data, error } = await supabase.rpc('update_movement_with_hardware', {
      p_date: movement.date,
      p_employee_uid: movement.employee_uid,
      p_foreman_uid: movement.foreman_uid,
      p_hardware_items: hardwares,
      p_movement_uid: movementUid,
      p_project_uid: movement.project_uid,
    });

    if (error) {
      console.error('Movement update error:', error);
      return createServiceResponse(null, error, ServiceDomains.Movement);
    }

    // Return the updated movement UID
    return { data, error: null };
  } catch (err: unknown) {
    console.error('Movement update exception:', err);
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Gets all movements
 * @returns ServiceResponse with movements data or error
 */
export async function getAll(params: MovementGetAllParams = {}): Promise<ServiceResponse<MovementListResponse[]>> {
  const { dateFrom, dateTo, textFilter = '', movementType = '', sortBy = '', sortOrder = '', pageNumber = null, pageSize = null } = params;
  try {
    let query = supabase.from('movements_list_view').select('*', { count: 'exact' }).is('deleted_at', null);

    if (textFilter && textFilter.trim()) {
      const trimmedTextFilter = textFilter.trim();
      query = query.or(
        `movement_id.ilike.%${trimmedTextFilter}%,project_id.ilike.%${trimmedTextFilter}%,project_description.ilike.%${trimmedTextFilter}%,employee_name.ilike.%${trimmedTextFilter}%,foreman_name.ilike.%${trimmedTextFilter}%`,
      );
    }

    if (dateFrom) {
      query = query.gte('movement_date', formatISO(dateFrom, { representation: 'date' }));
    }

    if (dateTo) {
      query = query.lte('movement_date', formatISO(dateTo, { representation: 'date' }));
    }

    if (movementType) {
      query = query.eq('movement_type', movementType);
    }

    if (sortBy) {
      const order = sortOrder || 'asc';
      query = query.order(sortBy, { ascending: order === 'asc' });
    }

    if (pageNumber != null && pageSize != null) {
      query = query.range((pageNumber - 1) * pageSize, pageNumber * pageSize - 1);
    }

    const { data, error: err, count } = await query;

    if (err) {
      const error = t(`errors.movements.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { count: count ?? 0, data: data as MovementListResponse[], error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

export async function deleteByUid(uid: string): Promise<ServiceResponse<string>> {
  try {
    const { data, error: err } = await supabase.rpc('delete_movement', { p_movement_uid: uid });

    if (err) {
      const error = t(`errors.movements.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

export async function getByUid(uid: string, options?: { includeHardwares?: boolean }): Promise<ServiceResponse<MovementResponse>> {
  const { includeHardwares = false } = options || {};

  try {
    let select = '*';
    if (includeHardwares) {
      select += `,
        movement_hardwares (
          *,
          hardwares(*, categories(*), sub_categories(*))
        )
      `;
    }

    const { data, error: err } = await supabase.from('movements').select<string, MovementResponse>(select).eq('uid', uid).single();

    if (err) {
      const error = t(`errors.movements.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}
